import { useState } from "react";
import { useOnMount } from "~/common/hooks/useOnMount";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

enum DROPDOWN_TYPE {
  ORDER_STATUS,
  ORDER_LINE_STAUTS,
  QUOTE_STATUS,
  QUOTE_LINE_STATUS,
}

export const ALL_CODE = "all";

const VARIANT_MAP: Dict<{ document: DROPDOWN_TYPE; lineItem: DROPDOWN_TYPE }> = {
  [SearchBoxVariant.ORDER]: {
    document: DROPDOWN_TYPE.ORDER_STATUS,
    lineItem: DROPDOWN_TYPE.ORDER_LINE_STAUTS,
  },
  [SearchBoxVariant.QUOTE]: {
    document: DROPDOWN_TYPE.QUOTE_STATUS,
    lineItem: DROPDOWN_TYPE.QUOTE_LINE_STATUS,
  },
};

export const useDropdownDataProvider = (variant: SearchBoxVariant) => {
  const { t } = useTranslation();
  const [orderStatuses, setOrderStatuses] = useState<DropdownEntry<string>[]>([]);
  const [lineItemStatuses, setLineItemStatuses] = useState<DropdownEntry<string>[]>([]);

  const [loading, setLoading] = useState(true);

  useOnMount(() => {
    (async () => {
      const [status, lineItemStatus] = await Promise.all([
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].document),
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].lineItem),
      ]);

      const transform = (item: { label: string; id: string }) => ({
        label: t(item.label),
        value: item.id,
      });

      setOrderStatuses(status.map(transform));
      setLineItemStatuses(lineItemStatus.map(transform));
      setLoading(false);
    })();
  });

  return {
    loading,
    orderStatuses,
    lineItemStatuses,
  };
};

const getOrderQuotesDropdownsOptions = (type: DROPDOWN_TYPE): Promise<any> => {
  let url = "/my-account";

  switch (type) {
    case DROPDOWN_TYPE.ORDER_STATUS:
      url += "/orderOptions";
      break;
    case DROPDOWN_TYPE.ORDER_LINE_STAUTS:
      url += "/orderLineOptions";
      break;
    case DROPDOWN_TYPE.QUOTE_STATUS:
      url += "/quoteOptions";
      break;
    case DROPDOWN_TYPE.QUOTE_LINE_STATUS:
      url += "/quoteLineOptions";
      break;
    default:
      return Promise.reject(new Error("Please provide a DROPDOWN TYPE parameter"));
  }

  url = resolveApiUrl(url);

  return fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "GET",
  })
    .then((data) => data.json())
    .then((data) =>
      data.map((item: DropdownOption) => ({ id: !item.id ? ALL_CODE : item.id, label: item.label }) as DropdownOption),
    );
};

interface DropdownOption {
  id: string;
  label: string;
}
